import api from './api';
import {
  AdminCreateUserRequest,
  AdminUpdateUserRequest,
  LoginRequest,
  SignupRequest,
  TokenResponse,
  User,
} from '../types';

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

/** Browser IANA timezone (e.g. "America/New_York"), or undefined if unavailable (T5). */
export function browserTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

export const authService = {
  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },

  setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
  },

  getStoredUser(): User | null {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as User;
    } catch {
      return null;
    }
  },

  setStoredUser(user: User): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },

  clearStorage(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },

  /** Persist a token + user from any TokenResponse (login or OTP verification). */
  applyToken(result: TokenResponse): User {
    this.setToken(result.access_token);
    this.setStoredUser(result.user);
    return result.user;
  },

  async login(data: LoginRequest): Promise<TokenResponse> {
    const res = await api.post<TokenResponse>('/api/auth/login', {
      ...data,
      timezone: browserTimezone(),
    });
    this.applyToken(res.data);
    return res.data;
  },

  async signup(data: SignupRequest): Promise<User> {
    const res = await api.post<User>('/api/auth/signup', data);
    return res.data;
  },

  /** Verify a signup OTP → auto-login (returns + persists a TokenResponse). */
  async verifyOtp(email: string, code: string): Promise<TokenResponse> {
    const res = await api.post<TokenResponse>('/api/auth/verify-otp', {
      email,
      code,
      timezone: browserTimezone(),
    });
    this.applyToken(res.data);
    return res.data;
  },

  async resendOtp(email: string): Promise<void> {
    await api.post('/api/auth/resend-otp', { email });
  },

  // ── Forgot / reset password (T6 / F3) ───────────────────────────────────────
  async forgotPassword(email: string): Promise<void> {
    await api.post('/api/auth/forgot-password', { email });
  },

  async resetPassword(email: string, code: string, newPassword: string): Promise<void> {
    await api.post('/api/auth/reset-password', { email, code, new_password: newPassword });
  },

  async logout(): Promise<void> {
    try {
      await api.post('/api/auth/logout');
    } finally {
      this.clearStorage();
    }
  },

  async me(): Promise<User> {
    const res = await api.get<User>('/api/auth/me');
    this.setStoredUser(res.data);
    return res.data;
  },

  // ── Admin (T3 / F2) ───────────────────────────────────────────────────────
  async adminListUsers(params?: {
    q?: string; role?: string; status?: string;
    includeDeleted?: boolean; limit?: number; offset?: number;
  }): Promise<{ users: User[]; total: number }> {
    const res = await api.get<{ users: User[]; total: number }>('/api/admin/users', {
      params: {
        q: params?.q || undefined,
        role: params?.role || undefined,
        status: params?.status || undefined,
        include_deleted: params?.includeDeleted ? true : undefined,
        limit: params?.limit,
        offset: params?.offset,
      },
    });
    return res.data;
  },

  async adminDeleteUser(id: string): Promise<User> {
    const res = await api.delete<User>(`/api/admin/users/${id}`);
    return res.data;
  },

  async adminRestoreUser(id: string): Promise<User> {
    const res = await api.post<User>(`/api/admin/users/${id}/restore`);
    return res.data;
  },

  async adminCreateUser(data: AdminCreateUserRequest): Promise<User> {
    const res = await api.post<User>('/api/admin/users', data);
    return res.data;
  },

  async adminUpdateUser(id: string, data: AdminUpdateUserRequest): Promise<User> {
    const res = await api.patch<User>(`/api/admin/users/${id}`, data);
    return res.data;
  },

  async adminSetPassword(id: string, newPassword: string): Promise<void> {
    await api.post(`/api/admin/users/${id}/set-password`, { new_password: newPassword });
  },
};
